import React, { useEffect, useRef } from "react"
import {
  Box,
  Input,
  Text,
  PopoverAnchor,
  InputGroup,
  Popover,
  type PopoverProps,
  InputLeftAddon,
  InputRightElement,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  InputLeftElement,
  Portal,
} from "@chakra-ui/react"
import { HexColorPicker } from "react-colorful"

type Props = {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  color: string
  onTextInputChange: (newColor: string) => void
  onColorPickerChange: (newColor: string) => void
  compact?: boolean
  disabled?: boolean
  placement?: PopoverProps["placement"]
}

const ColorSelectPopover = ({
  isOpen,
  onOpen,
  onClose,
  color,
  onColorPickerChange,
  onTextInputChange,
  compact,
  disabled,
  placement = "top",
}: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Anchor (not trigger) + open-on-focus, so a click into the field never toggles
  // the popover closed. Close on an outside pointer press rather than on blur:
  // react-colorful focuses its own slider on click, which would otherwise blur the
  // input and collapse the picker mid-selection. The check spans both the input and
  // the portaled content, so interacting with either keeps it open.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || contentRef.current?.contains(target)) {
        return
      }
      onClose()
    }

    document.addEventListener("mousedown", handlePointerDown)

    return () => document.removeEventListener("mousedown", handlePointerDown)
  }, [isOpen, onClose])

  return (
    <Popover
      variant="responsive"
      autoFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement={placement}
      closeOnBlur={false}
      strategy="fixed"
    >
      <Box ref={anchorRef}>
        <PopoverAnchor>
          <InputGroup mt={compact ? 0 : 4}>
            {compact && (
              <InputLeftAddon>
                <Text>Hex</Text>
              </InputLeftAddon>
            )}
            <InputLeftElement pl={compact ? 20 : 0} pointerEvents="none">
              <Text>#</Text>
            </InputLeftElement>
            <Input
              variant="outline"
              w={compact ? "full" : 28}
              bg="white"
              onFocus={onOpen}
              onChange={(e) => onTextInputChange(e.target.value)}
              value={color}
              sx={{ paddingLeft: 6 }}
              isDisabled={disabled}
            />
            {compact && (
              <InputRightElement>
                <Box
                  backgroundColor={`#${color}`}
                  borderWidth={1}
                  w={6}
                  h={6}
                  borderRadius="md"
                />
              </InputRightElement>
            )}
          </InputGroup>
        </PopoverAnchor>
      </Box>
      <Portal>
        <PopoverContent ref={contentRef} zIndex={1600}>
          <PopoverArrow />
          <PopoverBody>
            <HexColorPicker color={color} onChange={onColorPickerChange} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export default ColorSelectPopover
